/** Раскрытая деталь артикула — порт detailHtml (build-orders-dashboard-html.js,
 * стр. 5558-5575): шапка с рекомендацией и мини-KPI, сравнение маркетплейсов
 * (mpComparisonRows, стр. 5462-5497) и складская таблица с продажами по месяцам
 * (warehouseRows + warehouseTableWithMonthlySales, стр. 5329-5433). Складские
 * данные — ленивый запрос /api/orders/warehouses при раскрытии. Колонка «Кластер»
 * отложена вместе со справочником настроек. */
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import React, { useMemo } from "react";

import * as api from "@/api/orders";
import type { MpCode, OrdersMatrixReport } from "@/api/orders";
import * as planningApi from "@/api/orders-planning";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { expandRules, warehouseCluster } from "./clusters";
import { dec1, fmtDate, int0, monthLabel } from "./format";
import {
  daysBetween,
  recommendation,
  warehouseSupplySignal,
  type RowMetrics,
  type Tab,
  type ViewContext,
  type ViewRow,
} from "./metrics";

const MP_ORDER: { mp: MpCode; label: string }[] = [
  { mp: "ozon", label: "Ozon" },
  { mp: "wb", label: "Wildberries" },
  { mp: "ym", label: "Яндекс Маркет" },
];

const REC_CLS: Record<string, string> = {
  red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  blue: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
};

const SIGNAL_CLS: Record<string, string> = {
  danger: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  idle: "bg-muted text-muted-foreground",
  ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
};

function Chip({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="rounded-full border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
      {children}
    </span>
  );
}

function MiniCard({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="rounded-md border bg-background px-3 py-1.5 text-right">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/** Сравнение маркетплейсов: порт mpComparisonRows — все МП независимо от вкладки. */
function MpComparison({
  row,
  report,
  ctx,
}: {
  row: ViewRow;
  report: OrdersMatrixReport;
  ctx: ViewContext;
}): React.ReactElement {
  const months = ctx.months;
  const shownMonths = months.slice(-6);
  const rows = useMemo(() => {
    const byMp = new Map(
      MP_ORDER.map(({ mp }) => [
        mp,
        { qty: 0, amount: 0, months: months.map(() => 0), stock: 0 },
      ]),
    );
    for (const r of report.rows) {
      if (r.article !== row.article) continue;
      const entry = byMp.get(r.mp_code);
      if (!entry) continue;
      for (let i = 0; i < months.length; i += 1) {
        entry.months[i] = (entry.months[i] ?? 0) + (r.qty[i] ?? 0);
      }
      entry.qty += r.qty.reduce((s, v) => s + v, 0);
      entry.amount += r.total.reduce((s, v) => s + v, 0);
    }
    for (const s of report.stocks) {
      if (s.article !== row.article || !s.mp_code) continue;
      const entry = byMp.get(s.mp_code);
      if (entry) entry.stock += s.stock_mp;
    }
    const total = Math.max(1, [...byMp.values()].reduce((s, e) => s + e.qty, 0));
    return MP_ORDER.map(({ mp, label }) => {
      const e = byMp.get(mp)!;
      return {
        mp,
        label,
        ...e,
        avg: e.qty ? Math.round(e.amount / e.qty) : 0,
        share: (e.qty / total) * 100,
      };
    });
  }, [row.article, report, months]);

  return (
    <div className="overflow-x-auto rounded-md border bg-background">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="px-2 py-1.5 text-left font-medium">МП</th>
            {shownMonths.map((m) => (
              <th key={m} className="px-2 py-1.5 text-right font-medium">{monthLabel(m)}</th>
            ))}
            <th className="px-2 py-1.5 text-right font-medium">Итого</th>
            <th className="px-2 py-1.5 text-right font-medium">Доля</th>
            <th className="px-2 py-1.5 text-right font-medium">Ср. цена</th>
            <th className="px-2 py-1.5 text-right font-medium">Остаток</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.mp} className="border-b last:border-0">
              <td className="px-2 py-1.5 font-medium">{r.label}</td>
              {shownMonths.map((m) => {
                const i = months.indexOf(m);
                return (
                  <td key={m} className="px-2 py-1.5 text-right tabular-nums">
                    {int0(r.months[i] ?? 0)}
                  </td>
                );
              })}
              <td className="px-2 py-1.5 text-right font-medium tabular-nums">{int0(r.qty)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {r.share.toFixed(1).replace(".", ",")}%
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {r.avg ? `${int0(r.avg)} ₽` : "—"}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{int0(r.stock)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface WarehouseViewRow {
  mp_code: string;
  warehouse_id: string;
  warehouse_name: string;
  sales: number;
  amount: number;
  stock: number;
  storageFee: number;
  months: number[];
}

const MP_LABEL: Record<string, string> = { wb: "WB", ozon: "Ozon", ym: "ЯМ" };
const WAREHOUSE_LIMIT = 80;

/** Складская таблица: порт warehouseRows (слияние продаж/остатков/хранения по ключу
 * МП::склад) + warehouseTableWithMonthlySales. */
function WarehouseTable({
  report,
  tab,
  selectedDays,
}: {
  report: api.ArticleWarehousesReport;
  tab: Tab;
  selectedDays: number;
}): React.ReactElement {
  const months = report.months;
  const shownMonths = months.length > 18 ? months.slice(-18) : months;
  const clustersQ = useQuery({
    queryKey: ["orders", "refs", "clusters"],
    queryFn: planningApi.listClusters,
    staleTime: 60_000,
  });
  const rules = useMemo(() => expandRules(clustersQ.data ?? []), [clustersQ.data]);

  const allRows = useMemo(() => {
    const map = new Map<string, WarehouseViewRow>();
    const entryFor = (mp: string, id: string, name: string): WarehouseViewRow => {
      const key = `${mp}::${id}::${name}`;
      let e = map.get(key);
      if (!e) {
        e = {
          mp_code: mp, warehouse_id: id, warehouse_name: name,
          sales: 0, amount: 0, stock: 0, storageFee: 0,
          months: months.map(() => 0),
        };
        map.set(key, e);
      }
      return e;
    };
    for (const s of report.sales) {
      if (tab !== "all" && s.mp_code !== tab) continue;
      const i = months.indexOf(s.month);
      const e = entryFor(s.mp_code, s.warehouse_id, s.warehouse_name);
      e.sales += s.qty;
      e.amount += s.total;
      if (i >= 0) e.months[i] = (e.months[i] ?? 0) + s.qty;
    }
    for (const s of report.stocks) {
      if (tab !== "all" && s.mp_code !== tab) continue;
      entryFor(s.mp_code, s.warehouse_id, s.warehouse_name).stock += s.stock_mp;
    }
    for (const f of report.storage_fees) {
      // хранение — только WB (порт: mp_code || "wb")
      if (tab !== "all" && tab !== "wb") continue;
      entryFor("wb", f.warehouse_name, f.warehouse_name).storageFee += f.storage_fee;
    }
    return [...map.values()].sort(
      (a, b) =>
        b.storageFee - a.storageFee ||
        b.sales - a.sales ||
        b.stock - a.stock ||
        a.warehouse_name.localeCompare(b.warehouse_name, "ru"),
    );
  }, [report, tab, months]);

  if (!allRows.length) {
    return (
      <div className="rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground">
        Нет складской детализации по выбранному срезу.
      </div>
    );
  }
  const rows = allRows.slice(0, WAREHOUSE_LIMIT);

  return (
    <div className="space-y-1">
      {allRows.length > rows.length && (
        <div className="text-[11px] text-amber-600 dark:text-amber-400">
          Показано {int0(rows.length)} из {int0(allRows.length)} складских строк.
        </div>
      )}
      <div className="max-h-80 overflow-auto rounded-md border bg-background">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-background">
            <tr className="border-b text-muted-foreground">
              <th className="px-2 py-1.5 text-left font-medium">МП</th>
              <th className="px-2 py-1.5 text-left font-medium">Кластер</th>
              <th className="px-2 py-1.5 text-left font-medium">Склад</th>
              <th className="px-2 py-1.5 text-right font-medium">Продажи</th>
              <th className="px-2 py-1.5 text-right font-medium">Остаток</th>
              <th className="px-2 py-1.5 text-right font-medium">Запас, дн</th>
              <th className="px-2 py-1.5 text-right font-medium">Хранение, ₽</th>
              <th className="px-2 py-1.5 text-right font-medium">₽/шт</th>
              <th className="px-2 py-1.5 text-left font-medium">Подсказка</th>
              {shownMonths.map((m) => (
                <th key={m} className="px-2 py-1.5 text-right font-medium">{monthLabel(m)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const signal = warehouseSupplySignal(item, selectedDays);
              const perUnit = item.sales > 0 ? item.storageFee / item.sales : 0;
              const signalTitle =
                signal.stockDays === null
                  ? signal.action
                  : `${signal.action} · запас ${int0(signal.stockDays)} дн. · средние продажи ${dec1(signal.daily)} шт/день`;
              const cluster = warehouseCluster(item.mp_code, item.warehouse_name, rules);
              return (
                <tr key={`${item.mp_code}:${item.warehouse_id}:${item.warehouse_name}`} className="border-b last:border-0">
                  <td className="px-2 py-1">{MP_LABEL[item.mp_code] ?? item.mp_code}</td>
                  <td className="px-2 py-1">
                    <span
                      className="rounded px-1.5 py-0.5 text-[10px]"
                      style={{
                        backgroundColor: `${cluster.color}2e`,
                        border: `1px solid ${cluster.color}6b`,
                      }}
                    >
                      {cluster.name}
                    </span>
                  </td>
                  <td className="max-w-48 truncate px-2 py-1" title={item.warehouse_name}>
                    {item.warehouse_name || "Без склада"}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{int0(item.sales)}</td>
                  <td
                    className={cn(
                      "px-2 py-1 text-right tabular-nums",
                      item.stock <= 0 && item.sales > 0 && "font-medium text-red-600 dark:text-red-400",
                    )}
                  >
                    {int0(item.stock)}
                  </td>
                  <td
                    className="px-2 py-1 text-right font-medium tabular-nums"
                    style={{ backgroundColor: `${signal.range.color}22` }}
                    title={signal.range.label}
                  >
                    {signal.stockDays === null ? "—" : int0(signal.stockDays)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {item.storageFee ? int0(Math.round(item.storageFee)) : "—"}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {perUnit ? dec1(perUnit) : "—"}
                  </td>
                  <td className="px-2 py-1">
                    <span
                      className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", SIGNAL_CLS[signal.cls])}
                      title={signalTitle}
                    >
                      {signal.action}
                    </span>
                  </td>
                  {shownMonths.map((m) => {
                    const i = months.indexOf(m);
                    const v = i >= 0 ? (item.months[i] ?? 0) : 0;
                    return (
                      <td
                        key={m}
                        className={cn(
                          "px-2 py-1 text-right tabular-nums",
                          v > 0 && "bg-sky-50 dark:bg-sky-950/50",
                        )}
                      >
                        {v ? int0(v) : "—"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ArticleDetail({
  row,
  m,
  ctx,
  report,
  tab,
}: {
  row: ViewRow;
  m: RowMetrics;
  ctx: ViewContext;
  report: OrdersMatrixReport;
  tab: Tab;
}): React.ReactElement {
  const rec = recommendation(row, m);
  const selectedDays = daysBetween(ctx.dateFrom, ctx.dateTo);

  const wh = useQuery({
    queryKey: ["orders", "warehouses", row.article, ctx.dateFrom, ctx.dateTo],
    queryFn: () =>
      api.getArticleWarehouses(row.article, {
        date_from: ctx.dateFrom,
        date_to: ctx.dateTo,
      }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="space-y-3 px-3 py-3 text-xs" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="text-sm font-semibold">
            {row.article} · {row.name}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip>1С: {row.code_1c || row.id_1c || "—"}</Chip>
            <Chip>ABC/XYZ: {row.abcxyz}</Chip>
            <Chip>последняя продажа: {fmtDate(row.lastSale)}</Chip>
            <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", REC_CLS[rec.cls])}>
              {rec.title}
            </span>
          </div>
          <div className="text-muted-foreground">{rec.note}</div>
        </div>
        <div className="flex gap-2">
          <MiniCard label="Тек. месяц" value={int0(m.current)} />
          <MiniCard label="30 дней" value={int0(m.last30)} />
          <MiniCard label="Остаток МП" value={int0(row.stockMp)} />
          <MiniCard label="Остаток 1С" value={int0(row.stock1c)} />
        </div>
      </div>

      <div>
        <div className="mb-1 font-medium text-muted-foreground">
          Сравнение маркетплейсов: продажи, доля, цена и остаток
        </div>
        <MpComparison row={row} report={report} ctx={ctx} />
      </div>

      <div>
        <div className="mb-1 font-medium text-muted-foreground">
          Склады маркетплейсов: продажи по месяцам и остатки
        </div>
        {wh.isLoading && (
          <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-muted-foreground">
            <RefreshCw className="size-3.5 animate-spin" /> Загружаю склады…
          </div>
        )}
        {wh.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
            Склады недоступны: {extractErrorMessage(wh.error, "ошибка запроса")}
          </div>
        )}
        {wh.data && <WarehouseTable report={wh.data} tab={tab} selectedDays={selectedDays} />}
      </div>

      <ArticleHistory article={row.article} code1c={row.code_1c} />
    </div>
  );
}

/** «История изменений артикула» — последние 8 записей журнала с упоминанием артикула
 * (порт articleHistoryHtml, стр. 4819-4830). */
function ArticleHistory({
  article,
  code1c,
}: {
  article: string;
  code1c: string;
}): React.ReactElement | null {
  const log = useQuery({
    queryKey: ["orders", "log", "article", article],
    queryFn: () => planningApi.listLog({ search: article, limit: 8 }),
    staleTime: 30_000,
  });
  void code1c;
  if (!log.data?.items.length) return null;
  return (
    <div>
      <div className="mb-1 font-medium text-muted-foreground">История изменений артикула</div>
      <div className="rounded-md border bg-background">
        {log.data.items.map((item) => (
          <div key={item.id} className="flex gap-2 border-b px-2 py-1 text-[11px] last:border-0">
            <span className="shrink-0 text-muted-foreground">
              {new Date(item.ts).toLocaleString("ru-RU")}
            </span>
            <span className="shrink-0 font-medium">{item.username || "—"}</span>
            <span className="shrink-0">{item.action}</span>
            <span className="truncate text-muted-foreground" title={item.details}>
              {item.details}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
